import moment from 'moment';
import {Parser} from 'expr-eval';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import {fieldOptions as prequalFieldOptions} from '@/Core/mobx/ParameterStore/PrequalXref';
import {fieldOptions as approvalFieldOptions} from '@/Core/mobx/ParameterStore/ApprovalXref';
import {fieldOptions as healthFieldOptions} from '@/Core/mobx/ParameterStore/HealthXref';
import {fieldOptions as quoteFieldOptions} from '@/Core/mobx/ParameterStore/QuoteXref';
import {formatCurrency} from '../helpers/formatCurrency';
import {ApplicationData} from '@agenium-ai/agenium-client-sdk/dist/models';
import {CalcFocusData, Outlist} from '@/Agent/Quotes/InstantAppModal';

const SECTION_KEYS = ['prequal', 'approval', 'health', 'quote', 'banking'] as const;
type SectionKey = (typeof SECTION_KEYS)[number];

type FieldOptionsMap = Record<string, {options: string[] | number[]; optionValues?: string[]}>;

const SECTION_FIELD_OPTIONS: Partial<Record<SectionKey, FieldOptionsMap>> = {
    prequal: prequalFieldOptions as FieldOptionsMap,
    approval: approvalFieldOptions as FieldOptionsMap,
    health: healthFieldOptions as FieldOptionsMap,
    quote: quoteFieldOptions as FieldOptionsMap,
};

function resolveDisplayValue(section: SectionKey, camelField: string, rawValue: string): string {
    const sectionOpts = SECTION_FIELD_OPTIONS[section];
    if (!sectionOpts) return rawValue;
    const entry = Object.entries(sectionOpts).find(([k]) => k.toLowerCase() === camelField.toLowerCase())?.[1];
    if (!entry?.optionValues) return rawValue;
    const idx = entry.optionValues.indexOf(rawValue);
    const option = entry.options[idx];
    return idx !== -1 && option != null ? String(option) : rawValue;
}

// Matches tokens prefixed with a known section name: "approval_first_name", "prequal_smoker", etc.
const SECTION_PREFIX_RE = /^(prequal|approval|health|quote|banking)_(.+)$/;

function snakeToCamel(str: string): string {
    return str.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase());
}

// Splits an expression on the first top-level `??` (not inside brackets or parentheses).
// Returns [lhs, rhs] if found, or null if no top-level `??` exists.
function splitOnTopLevelNullCoalesce(expr: string): [string, string] | null {
    let depth = 0;
    for (let i = 0; i < expr.length - 1; i++) {
        const ch = expr[i];
        if (ch === '(' || ch === '[') depth++;
        else if (ch === ')' || ch === ']') depth--;
        else if (depth === 0 && ch === '?' && expr[i + 1] === '?') {
            return [expr.slice(0, i).trim(), expr.slice(i + 2).trim()];
        }
    }
    return null;
}

// Converts a human-readable spaced string to camelCase: "Face Amount" → "faceAmount"
function spacedToCamel(str: string): string {
    return str
        .split(/\s+/)
        .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
        .join('');
}

// Applied after a token's raw value is resolved (works for both ParameterStore and HubSpot tokens).
// Key: full token name (e.g. "hubspot_contact_lead_source", "approval_state")
// Value: (rawValue: string) => displayValue: string
const valueTransformers: Record<string, (value?: string | null) => string> = {
    hubspot_contact_channel: (value) => {
        const lower = value?.toLowerCase() ?? 'N/A';
        const allowedSubstrings = ['hancock', 'lincoln'];
        if (allowedSubstrings.some((s) => lower.includes(s))) return value ?? '';
        return 'N/A';
    },
    hubspot_contact_lead_source: (value) => {
        if (value?.toLowerCase() === 'john hancock') return 'John Hancock';
        return 'Amplify Life';
    },
    prequal_intent_focus: (value) => {
        return value?.toLowerCase() ?? '';
    },
    hubspot_contact_hs_latest_source: (value) => {
        switch (value) {
            case 'ORGANIC_SEARCH':
                return 'on Amplify Content';
            case 'PAID_SEARCH':
                return 'on a Search platform';
            case 'EMAIL_MARKETING':
                return 'on Email';
            case 'SOCIAL_MEDIA':
            case 'PAID_SOCIAL':
                return 'on a Social platform';
            case 'REFERRALS':
                return 'through a Referral';
            case 'AI_REFERRALS':
                return 'through an AI platform';
            case 'DIRECT_TRAFFIC':
                return 'on our Website';
            default:
                return '';
        }
    },
    hubspot_contact_createdate: (value) => {
        if (!value) return '';
        const m = moment(value);
        return m.isValid() ? m.format('MMMM Do, YYYY') : '';
    },
};

function applyTransform(key: string, value: string): string {
    const transform = valueTransformers[key];
    return transform ? transform(value) : value;
}

// Looks up an item in the calcFocusData.out array -- supports arithmetic expressions in array indices
function lookupOutItem(
    out: Outlist[],
    camelKey: string,
    indexExpr: string | null,
    lookupFn: (varName: string) => string | undefined,
): string | undefined {
    const match = out.find(
        (item) =>
            camelKey === (item as any).field || camelKey === item.fieldName || camelKey === spacedToCamel(item.name),
    );
    if (match == null) return undefined;

    if (indexExpr != null) {
        let index: number;
        try {
            const expr = new Parser().parse(indexExpr);
            const vars: Record<string, number> = {};
            for (const varName of expr.variables()) {
                const raw = lookupFn(varName);
                if (raw === undefined) return undefined;
                const num = parseFloat(raw);
                if (isNaN(num)) return undefined;
                vars[varName] = num;
            }
            const result = expr.evaluate(vars);
            if (typeof result !== 'number' || !isFinite(result)) return undefined;
            index = Math.round(result);
        } catch {
            return undefined;
        }
        const arr = Array.isArray(match.value) ? match.value : undefined;
        if (!arr) return undefined;
        const val = arr[index];
        return val != null ? String(val) : undefined;
    }

    return String(match.value);
}

function lookupParameterKey(
    key: string,
    parameterStore: ParameterStore,
    calcFocusData?: CalcFocusData,
): string | undefined {
    // cf_out_<key>[<indexExpr>] or cf_out_<key> — resolves from calcFocusData.out
    if (key.startsWith('cf_out_')) {
        const bracketStart = key.indexOf('[');
        const hasIndex = bracketStart !== -1;
        const baseKey = hasIndex ? key.slice(0, bracketStart) : key;
        const indexExpr = hasIndex ? key.slice(bracketStart + 1, key.lastIndexOf(']')) : null;
        const camelKey = snakeToCamel(baseKey.slice('cf_out_'.length));
        if (!calcFocusData?.out) return undefined;
        return lookupOutItem(calcFocusData.out, camelKey, indexExpr, (v) =>
            lookupParameterKey(v, parameterStore, calcFocusData),
        );
    }

    // cf_<field> — resolves from calcFocusData top-level fields (faceAmount, monthlyPremium, etc.)
    if (key.startsWith('cf_')) {
        const field = snakeToCamel(key.slice('cf_'.length));
        if (field === 'out') return undefined;
        const data = calcFocusData as Record<string, any> | null;
        if (!data) return undefined;
        const value = data[field];
        return value != null ? String(value) : undefined;
    }

    // quote_data_out_<key>[<indexExpr>] or quote_data_out_<key>
    if (key.startsWith('quote_data_out_')) {
        const bracketStart = key.indexOf('[');
        const hasIndex = bracketStart !== -1;
        const baseKey = hasIndex ? key.slice(0, bracketStart) : key;
        const indexExpr = hasIndex ? key.slice(bracketStart + 1, key.lastIndexOf(']')) : null;
        const camelKey = snakeToCamel(baseKey.slice('quote_data_out_'.length));
        const data = parameterStore.getLatestQuoteData();
        if (!data?.out) return undefined;
        return lookupOutItem(data.out, camelKey, indexExpr, (v) =>
            lookupParameterKey(v, parameterStore, calcFocusData),
        );
    }

    // quote_data_<field> — resolves from the active QuoteHistoryData (all fields except `out`)
    if (key.startsWith('quote_data_')) {
        const field = snakeToCamel(key.slice('quote_data_'.length));
        if (field === 'out') return undefined;
        const data = parameterStore.getLatestQuoteData() as Record<string, any> | null;
        if (!data) return undefined;
        const value = data[field];
        return value != null ? String(value) : undefined;
    }

    // Support section_field_name format (e.g. "approval_first_name" → approval.firstName)
    const sectionMatch = key.match(SECTION_PREFIX_RE);
    if (sectionMatch) {
        const section = sectionMatch[1] as SectionKey;
        const field = snakeToCamel(sectionMatch[2]);
        const param = (parameterStore[section] as Record<string, any>)[field];
        if (param && typeof param === 'object' && 'value' in param) {
            if (param.value == null) return undefined;
            return resolveDisplayValue(section, field, String(param.value));
        }
        return undefined;
    }

    // No section prefix — search all sections using the key converted to camelCase
    const camelKey = snakeToCamel(key);
    for (const section of SECTION_KEYS) {
        const param = (parameterStore[section] as Record<string, any>)[camelKey];
        if (param && typeof param === 'object' && 'value' in param) {
            if (param.value == null) return undefined;
            return resolveDisplayValue(section, camelKey, String(param.value));
        }
    }

    return undefined;
}

// Evaluates arithmetic expressions in tokens
function evaluateArithmetic(
    token: string,
    parameterStore: ParameterStore,
    calcFocusData?: CalcFocusData,
): string | undefined {
    try {
        const expr = new Parser().parse(token);
        const vars: Record<string, number> = {};
        for (const varName of expr.variables()) {
            const raw = lookupParameterKey(varName, parameterStore, calcFocusData);
            if (raw === undefined) return undefined;
            const num = parseFloat(raw);
            if (isNaN(num)) return undefined;
            vars[varName] = num;
        }
        const result = expr.evaluate(vars);
        if (typeof result !== 'number' || !isFinite(result)) return undefined;
        return Number.isInteger(result) ? String(result) : String(parseFloat(result.toFixed(2)));
    } catch {
        return undefined;
    }
}

// Evaluates a single expression token (field key, array index, or arithmetic) against ParameterStore.
// Does not handle `??` — callers split on that first.
function evaluateSingleToken(
    key: string,
    parameterStore: ParameterStore,
    calcFocusData?: CalcFocusData,
): string | undefined {
    if (/[+\-*/]/.test(key) || key.startsWith('(')) {
        return evaluateArithmetic(key, parameterStore, calcFocusData);
    }
    return lookupParameterKey(key, parameterStore, calcFocusData);
}

function isValidHubspotValue(value: string | undefined | null): boolean {
    return value !== undefined && value !== '' && value !== null && value !== 'N/A';
}

// Applies pre-resolved HubSpot values to a content string.
// Supports null coalescing: {{hubspot_contact_lead_source ?? hubspot_contact_channel}}
export function applyHubspotValues(
    type: 'contact' | 'deal',
    content: string,
    hubspotValues: Record<string, string>,
): string {
    const prefix = `hubspot_${type}_`;
    return content.replace(new RegExp(`\\{\\{hubspot_${type}_([^}]+)\\}\\}`, 'g'), (_match, prop: string) => {
        const parts = splitOnTopLevelNullCoalesce(prop.trim());
        if (parts) {
            const [lhsProp, rhsRaw] = parts;
            let lhsValue = hubspotValues[lhsProp];
            lhsValue = applyTransform(`${prefix}${lhsProp}`, lhsValue);
            if (isValidHubspotValue(lhsValue)) {
                return lhsValue;
            }
            // Strip the full prefix from RHS in case the author wrote the full token name
            const rhsProp = rhsRaw.startsWith(prefix) ? rhsRaw.slice(prefix.length) : rhsRaw;
            const rhsValue = hubspotValues[rhsProp];
            return applyTransform(`${prefix}${rhsProp}`, rhsValue ?? 'N/A');
        }
        const trimmedProp = prop.trim();
        const value = hubspotValues[trimmedProp];
        return applyTransform(`${prefix}${trimmedProp}`, value ?? 'N/A');
    });
}

// Applies pre-resolved ApplicationData values (from the Agenium SDK) to a content string.
// Token format: {{agenium_Case_UWDecision_Overall}} → applicationData.find(d => d.key === 'Case_UWDecision_Overall')?.value
// Currency modifier supported: {{$agenium_Case_Applicant_FaceAmountFinal}}
export function applyApplicationDataValues(content: string, applicationData: ApplicationData[]): string {
    return content.replace(/\{\{(\$?)agenium_([^}]+)\}\}/g, (match, currency: string, key: string) => {
        const trimmedKey = key.trim();
        const entry = applicationData.find((d) => d.key === trimmedKey);
        if (entry == null) return match;
        const raw = entry.value != null ? String(entry.value) : '';
        if (currency && !isNaN(Number(raw)) && raw !== '') return formatCurrency(Number(raw));
        return raw;
    });
}

// Resolves all ParameterStore tokens (with transforms)
export function interpolateParameterTokens(
    content: string,
    parameterStore: ParameterStore,
    calcFocusData?: CalcFocusData,
): string {
    return content.replace(/\{\{([^}]+)\}\}/g, (match, token: string) => {
        const raw = token.trim();
        const currencyFormat = raw.startsWith('$');
        const key = currencyFormat ? raw.slice(1) : raw;

        if (key.startsWith('hubspot_')) return match;

        // Null coalescing — must be checked before arithmetic since the full token may contain `*` or `+`
        const coalesceParts = splitOnTopLevelNullCoalesce(key);
        if (coalesceParts) {
            const [lhs, rhs] = coalesceParts;
            const lhsRaw = evaluateSingleToken(lhs, parameterStore, calcFocusData);
            const resolvedRaw = lhsRaw !== undefined ? lhsRaw : evaluateSingleToken(rhs, parameterStore, calcFocusData);
            if (resolvedRaw === undefined) return match;
            const resolvedKey = lhsRaw !== undefined ? lhs : rhs;
            const transformed = applyTransform(resolvedKey, resolvedRaw);
            if (currencyFormat && !isNaN(Number(transformed))) return formatCurrency(Number(transformed));
            return transformed;
        }

        if (/[+\-*/]/.test(key)) {
            const result = evaluateArithmetic(key, parameterStore, calcFocusData);
            if (result === undefined) return match;
            if (currencyFormat && !isNaN(Number(result))) return formatCurrency(Number(result));
            return result;
        }

        const value = lookupParameterKey(key, parameterStore, calcFocusData);
        if (value === undefined) return match;
        const transformed = applyTransform(key, value);
        if (currencyFormat && !isNaN(Number(transformed))) return formatCurrency(Number(transformed));
        return transformed;
    });
}
